/* =============================================================
   Speed Junkies HQ - customer support behaviours
   1. Accessible FAQ accordion
   2. "Message Our Team" contact form: client-side validation,
      duplicate-submit protection, draft restore after a failed post.
   No third-party dependencies. No credentials. No personal data
   is ever placed in a URL.
   ============================================================= */
(function () {
  'use strict';

  /* ---------------------------------------------------------- Accordion */
  function initAccordion(root) {
    var buttons = root.querySelectorAll('[data-sj-faq-toggle]');
    Array.prototype.forEach.call(buttons, function (button) {
      if (button.dataset.sjBound === 'true') return;
      button.dataset.sjBound = 'true';

      var panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;

      /* Progressive enhancement: answers are rendered open so the content is
         readable without JavaScript. Collapse them once the script runs. */
      if (button.getAttribute('aria-expanded') !== 'true') panel.hidden = true;

      button.addEventListener('click', function () {
        var open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      });
    });
  }

  /* Open (and scroll to) an FAQ item that is targeted by the URL hash. */
  function revealFromHash(rescroll) {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    var target;
    try {
      target = document.querySelector(hash);
    } catch (e) {
      return;
    }
    if (!target) return;

    var panel = target.closest ? target.closest('.sj-faq__answer') : null;
    if (panel && panel.hidden) {
      var opener = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (opener) opener.click();
    }

    /* Collapsing the accordion on load changes the page height, so the
       browser's own jump to the fragment lands in the wrong place. Redo it
       once, after the layout has settled. */
    if (rescroll) {
      var jump = function () {
        target.scrollIntoView({ block: 'start', behavior: 'instant' });
      };
      window.setTimeout(jump, 120);
      window.setTimeout(jump, 450);
    }
  }

  /* ------------------------------------------------------- Contact form */
  var STORAGE_KEY = 'sjContactDraft';

  var RULES = [
    { id: 'name', required: true, message: 'Enter your name so we know who to reply to.' },
    {
      id: 'email',
      required: true,
      message: 'Enter your email address.',
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
      patternMessage: 'Enter a valid email address, for example name@example.com.'
    },
    {
      id: 'phone',
      required: false,
      pattern: /^[0-9+()\-.\s]{7,20}$/,
      patternMessage: 'Enter a valid phone number, or leave this field empty.'
    },
    { id: 'body', required: true, message: 'Tell us how we can help.' }
  ];

  function fieldOf(form, id) {
    return form.querySelector('[data-sj-field="' + id + '"]');
  }

  function setError(form, id, message) {
    var input = fieldOf(form, id);
    var box = form.querySelector('[data-sj-error="' + id + '"]');
    if (!input || !box) return;
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      box.textContent = message;
      box.classList.add('is-visible');
    } else {
      input.removeAttribute('aria-invalid');
      box.textContent = '';
      box.classList.remove('is-visible');
    }
  }

  function validateField(form, rule) {
    var input = fieldOf(form, rule.id);
    if (!input) return true;
    var value = (input.value || '').trim();

    if (rule.required && !value) {
      setError(form, rule.id, rule.message);
      return false;
    }
    if (value && rule.pattern && !rule.pattern.test(value)) {
      setError(form, rule.id, rule.patternMessage);
      return false;
    }
    setError(form, rule.id, '');
    return true;
  }

  function saveDraft(form) {
    try {
      var draft = {};
      RULES.forEach(function (rule) {
        var input = fieldOf(form, rule.id);
        if (input) draft[rule.id] = input.value;
      });
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch (e) {
      /* storage unavailable - not fatal */
    }
  }

  function clearDraft() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function restoreDraft(form) {
    var raw;
    try {
      raw = window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;

    var draft;
    try {
      draft = JSON.parse(raw);
    } catch (e) {
      clearDraft();
      return;
    }

    Object.keys(draft).forEach(function (key) {
      var input = fieldOf(form, key);
      if (input && !input.value) input.value = draft[key];
    });
    clearDraft();
  }

  /* Preselect the topic from a ?topic= query string. The value is a plain
     support category - never personal information. */
  function applyTopicFromUrl(form) {
    var select = form.querySelector('[data-sj-topic]');
    if (!select) return;
    var requested;
    try {
      requested = new URLSearchParams(window.location.search).get('topic');
    } catch (e) {
      return;
    }
    if (!requested) return;
    var wanted = requested.toLowerCase().replace(/[^a-z]/g, '');
    Array.prototype.forEach.call(select.options, function (option) {
      if (option.value.toLowerCase().replace(/[^a-z]/g, '') === wanted) {
        select.value = option.value;
      }
    });
  }

  function initContactForm(root) {
    if (root.dataset.sjBound === 'true') return;
    root.dataset.sjBound = 'true';

    /* The Shopify {% form %} tag owns the <form> element, so the state flags
       live on an inner wrapper. Bind submit handling to the real form. */
    var form = root.closest('form') || root;
    form.dataset.sjPosted = root.dataset.sjPosted;
    form.dataset.sjHasErrors = root.dataset.sjHasErrors;

    /* Progressive enhancement: let the browser validate when JavaScript is
       unavailable, and use our own field-specific messages when it is. */
    form.setAttribute('novalidate', 'novalidate');

    var submit = form.querySelector('[data-sj-submit]');
    var succeeded = form.dataset.sjPosted === 'true';

    applyTopicFromUrl(form);

    if (succeeded) {
      clearDraft();
    } else if (form.dataset.sjHasErrors === 'true') {
      restoreDraft(form);
    }

    RULES.forEach(function (rule) {
      var input = fieldOf(form, rule.id);
      if (!input) return;
      input.addEventListener('blur', function () {
        if (input.value || rule.required) validateField(form, rule);
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') validateField(form, rule);
      });
    });

    form.addEventListener('submit', function (event) {
      if (form.dataset.sjSubmitting === 'true') {
        event.preventDefault();
        return;
      }

      var firstInvalid = null;
      RULES.forEach(function (rule) {
        var ok = validateField(form, rule);
        if (!ok && !firstInvalid) firstInvalid = fieldOf(form, rule.id);
      });

      /* Honeypot: a real person never fills this in. */
      var trap = form.querySelector('[data-sj-trap]');
      if (trap && trap.value) {
        event.preventDefault();
        return;
      }

      if (firstInvalid) {
        event.preventDefault();
        firstInvalid.focus();
        return;
      }

      form.dataset.sjSubmitting = 'true';
      saveDraft(form);
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        var busy = submit.getAttribute('data-busy-label');
        if (busy) submit.textContent = busy;
      }
    });
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sj-faq]'), initAccordion);
    Array.prototype.forEach.call(document.querySelectorAll('[data-sj-contact-form]'), initContactForm);
    revealFromHash(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('hashchange', revealFromHash);
  document.addEventListener('shopify:section:load', init);
})();
